import { HeroIcons } from '../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

const featureIcons = [
  HeroIcons.ChartBarSquareIcon,
  HeroIcons.PaperAirplaneIcon,
  HeroIcons.ShieldCheckIcon,
  HeroIcons.CpuChipIcon,
]

export default function BenefitsSection() {
  const { t } = useTranslation('index')
  const sectionRef = useRef<HTMLElement | null>(null)
  const hasAnimatedRef = useRef(false)
  const [isInView, setIsInView] = useState(false)
  const featureCopies = t('landing.benefits.features', {
    returnObjects: true,
  }) as Array<{ title: string; description: string }>
  const features = featureCopies.map((feature, index) => ({
    ...feature,
    icon: featureIcons[index] || HeroIcons.ChartBarSquareIcon,
  }))

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting || hasAnimatedRef.current) return

        hasAnimatedRef.current = true
        setIsInView(true)
        observer.disconnect()
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -18% 0px',
      },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      <div className="pointer-events-none absolute -right-24 top-4 h-56 w-56 rounded-full bg-[#d2daff]/35 blur-3xl tablet:h-72 tablet:w-72 desktop:hidden" />
      <div className="pointer-events-none absolute -left-28 top-80 h-48 w-48 rounded-full bg-[#eef2ff]/70 blur-3xl desktop:hidden" />

      <div className="relative mx-auto w-full px-3 tablet:px-10 desktop:px-44">
        <h2 className="max-w-xl px-3 font-semibold text-neutral-900 text-shelters-section-title tablet:px-0 desktop:max-w-none desktop:whitespace-nowrap">
          {t('landing.benefits.title.line1')}{' '}
          <span className="text-sheltersBlue">
            {t('landing.benefits.title.highlight')}
          </span>
        </h2>

        <div className="mt-9 grid grid-cols-1 gap-4 tablet:mt-12 tablet:grid-cols-2 tablet:gap-6 desktop:mt-20 desktop:grid-cols-4 desktop:gap-x-8 desktop:gap-y-0">
          {features.map((feature, index) => {
            const Icon = feature.icon

            return (
              <article
                key={feature.title}
                className={`group relative min-h-[198px] overflow-hidden rounded-card border border-[#cfd7ff] bg-[linear-gradient(160deg,rgba(255,255,255,0.92)_0%,rgba(239,243,255,0.78)_100%)] px-5 py-5 shadow-[0_6px_20px_rgba(51,40,154,0.06)] backdrop-blur-sm transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_10px_26px_rgba(51,40,154,0.11)] tablet:min-h-[230px] tablet:px-6 tablet:py-6 desktop:min-h-0 desktop:px-6 desktop:py-6 ${
                  isInView
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-4 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 90}ms` }}
              >
                <div className="pointer-events-none absolute right-6 top-6 h-12 w-12 rounded-full bg-[radial-gradient(circle,rgba(165,181,255,0.36)_0%,transparent_68%)] blur-sm desktop:right-5 desktop:top-5" />
                <div className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-[linear-gradient(90deg,transparent,rgba(165,181,255,0.36),transparent)] desktop:hidden" />

                <div className="inline-flex h-11 w-11 items-center justify-center rounded-inner-card bg-gradient text-white shadow-[0_10px_24px_rgba(51,40,154,0.24)] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 tablet:h-12 tablet:w-12 desktop:h-12 desktop:w-12">
                  <Icon
                    className="h-5 w-5 desktop:h-5 desktop:w-5"
                    strokeWidth={2.2}
                  />
                </div>

                <h3 className="mt-5 max-w-[270px] font-semibold text-sheltersDarkBlue text-shelters-benefit-title desktop:mt-7 desktop:max-w-[250px]">
                  {feature.title}
                </h3>
                <p className="mt-2.5 max-w-[290px] text-sheltersDarkBlue/80 text-shelters-benefit-body desktop:mt-4 desktop:max-w-[280px]">
                  {feature.description}
                </p>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
